import React from 'react';
import { errorService } from '@/lib/errorService';
import type {
  ContractDetail,
  ContractSearchResult,
  CustomerOption,
  NameType,
} from '../types';

/**
 * Builds the composite key for a customer option.
 * Centralizes key format to ensure consistency across the codebase.
 */
export function buildCustomerOptionKey(
  matchedName: string,
  matchedNameType: NameType
): string {
  return `${matchedName}|${matchedNameType}`;
}

/**
 * Builds an explicit display label for the customer dropdown.
 * Shows all relevant names with their types for clarity.
 *
 * @param buyerName - Primary buyer's display name (may be empty for business entities)
 * @param cobuyerName - Optional cobuyer name (included when cobuyer matched)
 * @param businessName - Optional associated business name (for individual buyers with a business)
 * @param matchedName - The name value that matched the search query (used for error reporting)
 * @param matchedNameType - Which name field matched the search query
 *
 * Examples:
 * - "Rick Summer (buyer)" - individual buyer matched
 * - "Rick's Auto (business)" - business customer matched (isBusiness=true)
 * - "Jane Smith (buyer) / Rick Summer (cobuyer)" - cobuyer matched
 * - "Test Example (buyer) / Rick Summer (business)" - associated business matched
 */
function buildDisplayLabel(
  buyerName: string,
  cobuyerName: string | undefined,
  businessName: string | undefined,
  matchedName: string,
  matchedNameType: NameType
): string {
  const buyerSubString = buyerName ? `${buyerName} (buyer)` : '';
  const cobuyerSubString = cobuyerName ? `${cobuyerName} (cobuyer)` : '';
  const businessSubString = businessName ? `${businessName} (business)` : '';
  const names = [buyerSubString, cobuyerSubString, businessSubString].filter(
    Boolean
  );

  if (names.length === 0) {
    errorService.reportError(
      new Error(
        `Could not build display label for matchedName: ${matchedName} and matchedNameType: ${matchedNameType}`
      ),
      {
        component: 'useCustomerSelection',
        matchedName,
        matchedNameType,
      }
    );
  }

  return names.filter(Boolean).join(' / ');
}

interface UseCustomerSelectionProps {
  /** The search results data containing contracts */
  data: ContractSearchResult | undefined;
  /** Optional name type from autocomplete selection for pre-selecting dropdown */
  searchedNameType?: NameType;
  /** Optional matched name from search for pre-selecting dropdown */
  searchedName?: string;
  /** Callback when customer selection needs to be cleared (e.g., on search mode change) */
  onClear?: () => void;
}

interface UseCustomerSelectionReturn {
  /** Currently selected customer option key (matchedName|matchedNameType) */
  selectedCustomer: string;
  /** Array of unique customer options for dropdown */
  uniqueCustomers: CustomerOption[];
  /** The currently selected customer option (for display/banner) */
  selectedOption: CustomerOption | null;
  /** Whether customer selection UI should be shown (multiple options exist) */
  shouldShowCustomerSelection: boolean;
  /** Filtered contracts based on selected customer option */
  displayedContracts: ContractDetail[];
  /** Function to handle customer selection change */
  handleCustomerSelectionChange: (optionKey: string) => void;
  /** Function to clear customer selection */
  clearCustomerSelection: () => void;
}

/**
 * Custom hook for managing customer selection logic in contract search.
 *
 * This hook groups contracts by matchedName + matchedNameType for disambiguation,
 * allowing users to switch between different match types (e.g., "Rick Summer (buyer)"
 * vs "Rick Summer (business)") without re-searching.
 *
 * When searchedNameType is provided (from autocomplete selection), the matching
 * option will be pre-selected in the dropdown.
 *
 * @param props Configuration object for customer selection behavior
 * @returns Object containing customer selection state and handlers
 */
export function useCustomerSelection({
  data,
  searchedNameType,
  searchedName,
  onClear,
}: UseCustomerSelectionProps): UseCustomerSelectionReturn {
  const [selectedCustomer, setSelectedCustomer] = React.useState<string>('');

  // Build unique customer options from search results
  // Key by matchedName + matchedNameType for disambiguation
  const uniqueCustomers = React.useMemo((): CustomerOption[] => {
    if (!data || data.status !== 'success') {
      return [];
    }

    // Map to collect unique options by key (matchedName|matchedNameType)
    const optionMap = new Map<string, CustomerOption>();
    const contractsWithoutMatch: string[] = [];

    data.contracts.forEach((contract) => {
      // Use matchedName/matchedNameType for disambiguation (VIN searches)
      // Fall back to buyerName for contract number searches
      const matchedName = contract.matchedName || contract.buyerName;
      const matchedNameType = contract.matchedNameType || 'buyer';
      const key = buildCustomerOptionKey(matchedName, matchedNameType);

      // Track contracts without match info for debugging
      if (!contract.matchedName && !contract.buyerName) {
        contractsWithoutMatch.push(contract.contractNumber);
      }

      // Only add if we haven't seen this key yet
      if (!optionMap.has(key)) {
        const displayLabel = buildDisplayLabel(
          contract.buyerName,
          contract.cobuyerName,
          contract.businessName,
          matchedName,
          matchedNameType
        );

        optionMap.set(key, {
          displayLabel,
          buyerName: contract.buyerName,
          cobuyerName: contract.cobuyerName,
          businessName: contract.businessName,
          isBusiness: contract.isBusiness,
          matchedName,
          matchedNameType,
          key,
        });
      }
    });

    // Report data quality issues if needed
    if (contractsWithoutMatch.length > 0) {
      errorService.reportError(
        new Error('Contracts missing match identification'),
        {
          component: 'useCustomerSelection',
          contractsWithoutMatch,
        }
      );
    }

    return Array.from(optionMap.values()).sort((a, b) =>
      a.displayLabel.localeCompare(b.displayLabel)
    );
  }, [data]);

  // Determine if customer selection should be shown
  const shouldShowCustomerSelection = uniqueCustomers.length > 1;

  // Get the currently selected option object
  const selectedOption = React.useMemo((): CustomerOption | null => {
    if (!selectedCustomer) return null;
    return uniqueCustomers.find((opt) => opt.key === selectedCustomer) || null;
  }, [selectedCustomer, uniqueCustomers]);

  // Filter displayed contracts based on selected customer option
  const displayedContracts = React.useMemo(() => {
    if (!data || data.status !== 'success') {
      return [];
    }

    if (!shouldShowCustomerSelection || !selectedCustomer) {
      // Single option or no selection - show all contracts
      return data.contracts;
    }

    const option = uniqueCustomers.find((opt) => opt.key === selectedCustomer);
    if (!option) {
      // Selection not found - this shouldn't happen
      errorService.reportError(
        new Error('Selected customer option not found'),
        {
          component: 'useCustomerSelection',
          selectedCustomer,
          availableKeys: uniqueCustomers.map((o) => o.key),
        }
      );
      return [];
    }

    // Filter contracts by the selected matchedName + matchedNameType
    // Use case-insensitive comparison for consistency with pre-selection logic
    return data.contracts.filter((contract) => {
      if (contract.matchedName && contract.matchedNameType) {
        // VIN search - filter by matchedName and matchedNameType
        return (
          contract.matchedName.toLowerCase() ===
            option.matchedName.toLowerCase() &&
          contract.matchedNameType === option.matchedNameType
        );
      }
      // Contract number search - filter by buyerName (fallback)
      return (
        contract.buyerName.toLowerCase() === option.buyerName.toLowerCase()
      );
    });
  }, [data, selectedCustomer, shouldShowCustomerSelection, uniqueCustomers]);

  const handleCustomerSelectionChange = React.useCallback(
    (optionKey: string) => {
      setSelectedCustomer(optionKey);
    },
    []
  );

  const clearCustomerSelection = React.useCallback(() => {
    setSelectedCustomer('');
    onClear?.();
  }, [onClear]);

  // Auto-select matching option when searchedNameType is provided (from autocomplete)
  // or auto-select when there's only one option
  React.useEffect(() => {
    // Clear selection when data changes (new search)
    setSelectedCustomer('');

    if (!data || uniqueCustomers.length === 0) {
      return;
    }

    // If only one option, auto-select it
    if (uniqueCustomers.length === 1) {
      setSelectedCustomer(uniqueCustomers[0].key);
      return;
    }

    // If searchedNameType was provided (from autocomplete), pre-select matching option
    if (searchedNameType && searchedName) {
      const normalizedSearchName = searchedName.toLowerCase();
      const matchingOption = uniqueCustomers.find(
        (opt) =>
          opt.matchedNameType === searchedNameType &&
          opt.matchedName.toLowerCase().includes(normalizedSearchName)
      );
      if (matchingOption) {
        setSelectedCustomer(matchingOption.key);
      } else {
        // Log when expected pre-selection failed - helps debug autocomplete/search mismatches
        errorService.reportError(
          new Error(
            'Could not pre-select customer option from autocomplete selection'
          ),
          {
            component: 'useCustomerSelection',
            searchedNameType,
            searchedName,
            availableOptions: uniqueCustomers.map((o) => ({
              matchedName: o.matchedName,
              matchedNameType: o.matchedNameType,
            })),
          }
        );
      }
    }
  }, [data, uniqueCustomers, searchedNameType, searchedName]);

  return {
    selectedCustomer,
    uniqueCustomers,
    selectedOption,
    shouldShowCustomerSelection,
    displayedContracts,
    handleCustomerSelectionChange,
    clearCustomerSelection,
  };
}
